import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pct = (v) => (v == null ? "—" : `${(v * 100).toFixed(0)}%`);
const usd = (v) => (v == null ? "—" : `${v >= 0 ? "+" : ""}$${Number(v).toFixed(2)}`);

const Box = ({ label, value, tone, testid }) => (
  <div className="border border-slate-800 p-1.5">
    <div className="text-slate-500 label-uppercase text-[9px]">{label}</div>
    <div className={`font-mono truncate ${tone || "text-slate-100"}`} data-testid={testid}>{value}</div>
  </div>
);

const BucketTable = ({ title, rows, testid }) => (
  <div data-testid={testid}>
    <div className="text-slate-500 label-uppercase text-[9px] mb-1">{title}</div>
    {rows?.length ? rows.slice(0, 6).map((r) => (
      <div key={r.key} className="flex items-center justify-between gap-2 py-0.5 border-b border-slate-800/50 font-mono text-[10px]">
        <span className="truncate text-slate-200">{String(r.key).replace("-USDT-SWAP", "")}</span>
        <span className="flex gap-2 shrink-0">
          <span className="text-slate-500">{r.trades}t</span>
          <span className={r.win_rate >= 0.5 ? "text-emerald-300" : "text-red-300"}>{pct(r.win_rate)}</span>
          <span className={r.pnl >= 0 ? "text-emerald-300" : "text-red-300"}>{usd(r.pnl)}</span>
          {r.blocked ? <span className="text-amber-300">BLOCKED</span> : null}
        </span>
      </div>
    )) : <div className="text-slate-600 text-[10px]">No trades yet</div>}
  </div>
);

export const LearningPanel = () => {
  const [st, setSt] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/learning/status`);
      setSt(r.data);
    } catch (e) {
      // keep last snapshot
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, []);

  const reset = async () => {
    if (!window.confirm("Forget everything the bot has learned from past trades?")) return;
    try {
      await axios.post(`${API}/learning/reset`);
      toast.success("Learning reset");
      load();
    } catch (e) {
      toast.error("Reset failed");
    }
  };

  const seed = async () => {
    try {
      const r = await axios.post(`${API}/learning/seed-backtest`);
      toast.success(r.data?.message || "Backtest learning started");
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start backtest learning");
    }
  };

  const off = st?.offsets || {};
  const meta = st?.meta_model || {};
  const fee = st?.fee || {};
  return (
    <div className="border border-slate-700/60 bg-slate-900/40 p-3 space-y-3" data-testid="learning-panel">
      <div className="flex items-center justify-between">
        <span className="label-uppercase text-cyan-300">Self-Learning</span>
        <span
          className={`label-uppercase text-[9px] px-1.5 py-0.5 border ${st?.enabled ? "text-emerald-300 border-emerald-500/60" : "text-slate-400 border-slate-600"}`}
          data-testid="learning-enabled"
        >
          {st?.enabled ? "LEARNING ON" : "OFF"}
        </span>
      </div>
      <div className="text-[10px] text-slate-500">
        Learns from every closed trade: penalises or blocks losing coins / strategies / hours, rewards winners,
        and tightens or relaxes entry bars. Never changes your stop loss, profit lock or risk.
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Box label="Trades learned" value={st?.trades ?? 0} testid="learning-trades" />
        <Box label="Win rate (last 20)" value={pct(st?.recent_win_rate)} testid="learning-recent-wr" />
        <Box label="Total PnL" value={usd(st?.total_pnl ?? 0)} tone={(st?.total_pnl ?? 0) >= 0 ? "text-emerald-300" : "text-red-300"} testid="learning-total-pnl" />
        <Box label="Min score" value={`${st?.base_min_entry_score ?? "—"} ${off.min_entry_score ? `(${off.min_entry_score > 0 ? "+" : ""}${off.min_entry_score})` : ""}`} testid="learning-score-offset" />
        <Box label="Min confluence" value={`${st?.base_min_confluence_score ?? "—"} ${off.min_confluence_score ? `(${off.min_confluence_score > 0 ? "+" : ""}${off.min_confluence_score})` : ""}`} testid="learning-conf-offset" />
        <Box
          label="Win model"
          value={meta.trained ? `ON · ${pct(meta.fit_accuracy)}` : `${meta.have ?? st?.trades ?? 0}/${meta.needs ?? 30}`}
          tone={meta.trained ? "text-emerald-300" : "text-slate-400"}
          testid="learning-meta"
        />
        <Box
          label="Backtest trades"
          value={`${st?.backtest_trades ?? 0}`}
          testid="learning-backtest-trades"
        />
        <Box
          label="Fee / side"
          value={fee.rate_pct != null ? `${fee.rate_pct}% (${fee.source})` : "unknown"}
          tone={fee.rate_pct != null ? "text-slate-100" : "text-amber-300"}
          testid="learning-fee-rate"
        />
        <Box label="Fee-aware" value={fee.fee_aware ? "ON" : "OFF"} tone={fee.fee_aware ? "text-emerald-300" : "text-slate-400"} testid="learning-fee-aware" />
      </div>
      {fee.fee_aware && fee.rate_pct == null ? (
        <div className="text-[10px] text-amber-300" data-testid="learning-fee-warning">
          Fee rate unknown (OKX not connected). Enter your fee % in Stop Loss &amp; Profit Lock so limits count fees.
        </div>
      ) : null}
      <button
        onClick={seed}
        disabled={st?.seeding}
        className="w-full label-uppercase text-[10px] py-1.5 border border-cyan-500/50 text-cyan-300 transition-colors hover:bg-cyan-950/40 disabled:opacity-50"
        data-testid="learning-seed-btn"
      >
        {st?.seeding ? "Learning from backtest…" : "Learn From Backtest Now"}
      </button>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <BucketTable title="By strategy" rows={st?.by_strategy} testid="learning-by-strategy" />
        <BucketTable title="By coin" rows={st?.by_symbol} testid="learning-by-symbol" />
        <BucketTable title="By side" rows={st?.by_side} testid="learning-by-side" />
        <BucketTable title="By hour (UTC)" rows={st?.by_hour} testid="learning-by-hour" />
      </div>
      <div>
        <div className="text-slate-500 label-uppercase text-[9px] mb-1">Learning log</div>
        <div className="max-h-28 overflow-y-auto space-y-0.5" data-testid="learning-log">
          {st?.log?.length ? st.log.map((l, i) => (
            <div key={i} className="text-[10px] text-slate-300">
              <span className="text-slate-600 font-mono">{new Date(l.ts * 1000).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span> {l.msg}
            </div>
          )) : <div className="text-slate-600 text-[10px]">Nothing adjusted yet — learning starts after the first closed trades</div>}
        </div>
      </div>
      <button
        onClick={reset}
        className="w-full label-uppercase text-[10px] py-1.5 border border-slate-600 text-slate-400 transition-colors hover:border-red-500/60 hover:text-red-300"
        data-testid="learning-reset-btn"
      >
        Reset Learning
      </button>
    </div>
  );
};
